import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Send, Loader2, MessageSquare, Sparkles, Lightbulb, Brain, ShieldAlert } from 'lucide-react';
import { useAuth } from 'contexts/AuthContext';

/* ─── AGENTES IA (Colores adaptados a Neón/Cyberpunk) ────────────────────── */
const DID_AGENTS = [
  {
    name: 'Laura',
    role: 'Tutor IA Personal',
    photo: '/tutor.jpg',
    glowA: '#FF00FF', glowB: '#00FFFF',
    accentColor: '#FF00FF', gradientFrom: '#4a004a', gradientTo: '#FF00FF',
    personality: 'amigable y motivadora',
  },
  {
    name: 'María',
    role: 'Tutora de Formación',
    photo: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=400&fit=crop&crop=face&auto=format',
    glowA: '#00FFFF', glowB: '#3B82F6',
    accentColor: '#00FFFF', gradientFrom: '#003366', gradientTo: '#00FFFF',
    personality: 'empática y detallista',
  },
  {
    name: 'Carlos',
    role: 'Coach de Desarrollo',
    photo: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=400&h=400&fit=crop&crop=face&auto=format',
    glowA: '#ccff00', glowB: '#10B981',
    accentColor: '#ccff00', gradientFrom: '#334d00', gradientTo: '#ccff00',
    personality: 'dinámico y práctico',
  },
  {
    name: 'Sofía',
    role: 'Asesora de Aprendizaje',
    photo: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=400&h=400&fit=crop&crop=face&auto=format',
    glowA: '#FF3366', glowB: '#FF9933',
    accentColor: '#FF3366', gradientFrom: '#660022', gradientTo: '#FF3366',
    personality: 'cálida y estructurada',
  },
  {
    name: 'Javier',
    role: 'Mentor de Habilidades',
    photo: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face&auto=format',
    glowA: '#FF9900', glowB: '#FFCC00',
    accentColor: '#FF9900', gradientFrom: '#663300', gradientTo: '#FF9900',
    personality: 'experimentado y directo',
  },
  {
    name: 'Laura',
    role: 'Especialista en Formación',
    photo: 'https://images.unsplash.com/photo-1594744803329-e58b31de8bf5?w=400&h=400&fit=crop&crop=face&auto=format',
    glowA: '#8A2BE2', glowB: '#00FFFF',
    accentColor: '#8A2BE2', gradientFrom: '#2E0854', gradientTo: '#8A2BE2',
    personality: 'organizada y clara',
  },
];

const getAgentForOrg = (orgId) => {
  if (!orgId) return DID_AGENTS[0];
  const hash = orgId.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return DID_AGENTS[hash % DID_AGENTS.length];
};

/* ─── GLOBAL STYLES ──────────────────────────────────────────────────────── */
const WidgetStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
    
    @keyframes smRingRotate { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
    @keyframes smFadeUp { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
    @keyframes smBounce { 0%,80%,100%{transform:scale(0)} 40%{transform:scale(1)} }
    @keyframes smPop { 0%{transform:scale(0.85);opacity:0} 100%{transform:scale(1);opacity:1} }
    
    .sm-ring { animation: smRingRotate 4s linear infinite; }
    .sm-fade-up { animation: smFadeUp 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
    .sm-pop { animation: smPop 0.3s cubic-bezier(0.34,1.56,0.64,1) forwards; }
    .sm-dot1 { animation: smBounce 1.2s infinite 0ms; }
    .sm-dot2 { animation: smBounce 1.2s infinite 160ms; }
    .sm-dot3 { animation: smBounce 1.2s infinite 320ms; }

    .cyber-bot-font { font-family: 'DM Sans', sans-serif; }

    /* Custom Scrollbar for Chat */
    .chat-scroll::-webkit-scrollbar { width: 6px; }
    .chat-scroll::-webkit-scrollbar-track { background: rgba(255,255,255,0.02); }
    .chat-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 4px; }
    .chat-scroll::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.3); }
    
    /* Sugestiones ocultar scrollbar */
    .hide-scroll::-webkit-scrollbar { display: none; }
    .hide-scroll { -ms-overflow-style: none; scrollbar-width: none; }
  `}</style>
);

const AgentAvatarSmall = ({ agent, size = 40 }) => {
  const [err, setErr] = useState(false);
  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <div
        className="sm-ring absolute inset-0 rounded-full"
        style={{
          background: `conic-gradient(${agent.glowA}, ${agent.glowB}, transparent, ${agent.glowA})`,
          padding: 2,
        }}
      />
      <div className="absolute rounded-full bg-[#060810]" style={{ inset: 2 }} />
      {!err ? (
        <img
          src={agent.photo}
          alt={agent.name}
          className="absolute rounded-full object-cover object-top opacity-90"
          style={{ inset: 3, width: size - 6, height: size - 6 }}
          onError={() => setErr(true)}
        />
      ) : (
        <div
          className="absolute rounded-full flex items-center justify-center text-white font-black"
          style={{
            inset: 3, width: size - 6, height: size - 6,
            background: `linear-gradient(135deg, ${agent.gradientFrom}, ${agent.gradientTo})`,
            fontSize: size * 0.35,
          }}
        >
          {agent.name[0]}
        </div>
      )}
      <div
        className="absolute rounded-full border-2 border-[#060810] shadow-[0_0_8px_#ccff00]"
        style={{
          width: size * 0.28, height: size * 0.28,
          bottom: 0, right: 0,
          background: '#ccff00',
        }}
      />
    </div>
  );
};

const Bubble = ({ msg, agent }) => {
  const isUser = msg.role === 'user';
  return (
    <div className={`flex gap-3 sm-fade-up ${isUser ? 'flex-row-reverse' : ''}`}>
      {!isUser && <AgentAvatarSmall agent={agent} size={36} />}
      <div
        className={`max-w-[85%] px-4 py-3 text-[13.5px] leading-relaxed ${
          isUser ? 'rounded-2xl rounded-tr-sm' : 'rounded-2xl rounded-tl-sm'
        }`}
        style={
          isUser
            ? {
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: 'white',
              }
            : {
                background: `${agent.accentColor}1A`,
                border: `1px solid ${agent.accentColor}4D`,
                color: 'white',
                boxShadow: `0 4px 20px ${agent.glowA}11`,
              }
        }
      >
        {!isUser && (
          <p className="text-[10px] font-black mb-1.5 uppercase tracking-widest flex items-center gap-1.5" style={{ color: agent.accentColor }}>
            <Brain className="w-3 h-3" /> {agent.name}
          </p>
        )}
        <p className="whitespace-pre-wrap font-medium opacity-90">{msg.content}</p>
      </div>
    </div>
  );
};

const TypingDots = ({ agent }) => (
  <div className="flex gap-3 sm-fade-up">
    <AgentAvatarSmall agent={agent} size={36} />
    <div
      className="flex items-center gap-1.5 px-5 py-4 rounded-2xl rounded-tl-sm"
      style={{ background: `${agent.accentColor}1A`, border: `1px solid ${agent.accentColor}4D` }}
    >
      <span className="w-1.5 h-1.5 rounded-full sm-dot1" style={{ background: agent.accentColor, boxShadow: `0 0 8px ${agent.accentColor}` }} />
      <span className="w-1.5 h-1.5 rounded-full sm-dot2" style={{ background: agent.accentColor, boxShadow: `0 0 8px ${agent.accentColor}` }} />
      <span className="w-1.5 h-1.5 rounded-full sm-dot3" style={{ background: agent.accentColor, boxShadow: `0 0 8px ${agent.accentColor}` }} />
    </div>
  </div>
);

const FABButton = ({ agent, open, onClick, isTestMode, hasUnread }) => (
  <button
    onClick={onClick}
    className="fixed bottom-6 right-6 z-50 transition-all hover:scale-110 active:scale-95 group"
    style={{ filter: `drop-shadow(0 0 20px ${agent.glowA}88)` }}
    aria-label="Abrir asistente IA"
  >
    <div className="relative w-16 h-16">
      <div
        className="sm-ring absolute inset-0 rounded-full opacity-70 group-hover:opacity-100 transition-opacity"
        style={{
          background: `conic-gradient(${agent.glowA}, ${agent.glowB}, transparent, ${agent.glowA})`,
          padding: 3,
        }}
      />
      <div
        className="absolute rounded-full flex items-center justify-center border border-white/20"
        style={{
          inset: 3,
          background: open
            ? '#060810'
            : `linear-gradient(135deg, ${agent.gradientFrom}, ${agent.gradientTo})`,
          transition: 'background 0.3s',
        }}
      >
        {open ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <Brain className="w-7 h-7 text-white" />
        )}
      </div>
      {!open && isTestMode && (
        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center font-black border-2 border-[#060810] shadow-[0_0_10px_red] animate-pulse">!</span>
      )}
      {!open && hasUnread && !isTestMode && (
        <span
          className="absolute -top-1 -right-1 w-4 h-4 rounded-full border-2 border-[#060810] animate-pulse"
          style={{ background: '#ccff00', boxShadow: '0 0 10px #ccff00' }}
        />
      )}
    </div>
  </button>
);

// ─── MAIN WIDGET ──────────────────────────────────────────────────────────────
const MiniBotWidget = ({ title, content, sections, isTestMode }) => {
  const { user, api } = useAuth();   // ← api añadido aquí, en el render
  const agent = getAgentForOrg(user?.organization_id);
  const locked = !user?.features?.ai_tutor;
  const [open, setOpen]       = useState(false);
  const [msgs, setMsgs]       = useState([]);
  const [input, setInput]     = useState('');
  const [loading, setLoading] = useState(false);
  const [hasUnread, setHasUnread] = useState(true);
  const [suggestions, setSuggestions] = useState(
    isTestMode
      ? ['¿Puedes darme una pista conceptual?', 'No entiendo esta premisa', '¿Qué debo deducir?']
      : ['¿De qué trata este bloque?', 'Explícame el concepto central', '¿Qué datos debo priorizar?']
  );
  
  const bottomRef  = useRef(null);
  const inputRef   = useRef(null);
  
  // ── Ref para leer input dentro de send sin añadirlo como dep ──
  const inputValueRef = useRef(input);
  useEffect(() => { inputValueRef.current = input; }, [input]);

  const agentNameRef   = useRef(agent.name);
  const isTestModeRef  = useRef(isTestMode);
  const titleRef       = useRef(title);

  // ── Init greeting ──
  useEffect(() => {
    const agentName  = agentNameRef.current;
    const testMode   = isTestModeRef.current;
    const moduleTitle = titleRef.current;

    const greeting = testMode
      ? `Protocolo de Evaluación activo. Soy ${agentName}. Supervisaré tu desempeño y puedo ofrecer orientación teórica sobre el bloque "${moduleTitle}". Las resoluciones directas están bloqueadas.`
      : `Sistema Neuronal iniciado. Soy ${agentName}, tu enlace para el módulo "${moduleTitle}". ¿En qué parámetro necesitas asistencia hoy?`;

    setMsgs([{ role: 'assistant', content: greeting }]);
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [msgs, loading]);

  useEffect(() => {
    if (open) {
      setHasUnread(false);
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open]);

  const send = useCallback(async (text) => {
    const msg = (text !== undefined ? text : inputValueRef.current || '').trim();
    if (!msg || loading || locked) return;
    setInput('');
    setMsgs(p => [...p, { role: 'user', content: msg }]);
    setLoading(true);

    try {
      const moduleContent = content 
        || sections?.map(s => `## ${s.title}\n${s.content}`).join('\n\n') 
        || '';

      const systemContext = isTestMode
        ? `Eres ${agent.name}, tutor ${agent.personality}. El usuario está haciendo un examen sobre "${title}".
CONTENIDO DEL MÓDULO AL QUE DEBES LIMITARTE:
${moduleContent}
REGLAS: NO des respuestas directas a preguntas del examen. Solo pistas conceptuales basadas en el contenido anterior. Si preguntan algo ajeno al módulo, responde: "Solo puedo ayudarte con el módulo '${title}'"` 
        : `Eres ${agent.name}, tutor ${agent.personality}. Ayudas al usuario EXCLUSIVAMENTE con el siguiente módulo:
TÍTULO: "${title}"
CONTENIDO:
${moduleContent}
REGLAS: Responde SOLO preguntas relacionadas con este contenido. Si preguntan algo ajeno, responde: "Solo puedo ayudarte con el módulo '${title}'". Sé conciso y pedagógico. Siempre en español.`;

      const res = await api.post('/ai/chat', { message: msg, context: systemContext });
      const data = res.data;
      setMsgs(p => [...p, { role: 'assistant', content: data.response || 'Anomalía en la red. Interfaz no disponible.' }]);
      if (data.suggestions?.length > 0) setSuggestions(data.suggestions.slice(0, 3));
    } catch {
      setMsgs(p => [...p, { role: 'assistant', content: 'Fallo de conexión en el enlace neuronal. Reinténtalo.' }]);
    } finally {
      setLoading(false);
    }
  }, [agent.name, agent.personality, content, sections, isTestMode, loading, title, api]); // ← api añadido a las deps

  return (
    <div className="cyber-bot-font">
      <WidgetStyles />

      {open && (
        <div
          className="fixed bottom-24 right-6 z-50 sm-pop flex flex-col overflow-hidden"
          style={{
            width: 360,
            height: 520,
            borderRadius: 24,
            background: '#060810',
            border: `1px solid ${agent.glowA}4D`,
            boxShadow: `0 10px 50px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.05), 0 0 40px ${agent.glowA}22`,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)'
          }}
        >
          {/* Header */}
          <div
            className="flex items-center gap-3 px-5 py-4 flex-shrink-0"
            style={{
              background: `linear-gradient(90deg, ${agent.gradientFrom}4D, transparent)`,
              borderBottom: `1px solid ${agent.glowA}33`
            }}
          >
            <AgentAvatarSmall agent={agent} size={42} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <p className="text-white font-black text-sm tracking-wide">{agent.name}</p>
                <Sparkles className="w-3.5 h-3.5" style={{ color: agent.accentColor }} />
              </div>
              <p className="text-white/50 text-[10px] uppercase font-bold tracking-widest truncate">{agent.role}</p>
              <div className="flex items-center gap-1.5 mt-1">
                <span className="w-1.5 h-1.5 rounded-full animate-pulse shadow-[0_0_8px_#ccff00]" style={{ background: '#ccff00' }} />
                <span className="text-white/40 text-[9px] uppercase tracking-wider font-bold">
                  {isTestMode ? 'Supervisión Activa' : 'Enlace Establecido'}
                </span>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-white/40 hover:text-white transition-colors flex-shrink-0 p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {isTestMode && (
            <div
              className="flex items-center gap-2 px-5 py-2.5 text-[10px] font-black uppercase tracking-widest flex-shrink-0"
              style={{ background: 'rgba(239, 68, 68, 0.1)', borderBottom: '1px solid rgba(239, 68, 68, 0.3)', color: '#F87171' }}
            >
              <ShieldAlert className="w-3.5 h-3.5" />
              Restricción: Respuestas directas bloqueadas
            </div>
          )}
           {locked ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 p-8 text-center">
              <Sparkles className="w-8 h-8" style={{ color: agent.accentColor }} />
              <p className="font-black text-sm uppercase tracking-widest" style={{ color: agent.accentColor }}>
                Asistente no incluido en tu plan
              </p>
              <p className="text-white/60 text-xs leading-relaxed">
                Habla con el administrador de tu empresa para activar el tutor IA.
              </p>
            </div>
          ) : (
            <>

          {/* Messages */}
          <div
            className="flex-1 overflow-y-auto p-5 space-y-4 chat-scroll"
            style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.02) 0%, transparent 100%)' }}
          >
            {msgs.map((m, i) => <Bubble key={i} msg={m} agent={agent} />)}
            {loading && <TypingDots agent={agent} />}
            <div ref={bottomRef} />
          </div>

          {/* Suggestions */}
          {!loading && suggestions.length > 0 && (
            <div
              className="px-4 py-3 flex gap-2 overflow-x-auto hide-scroll flex-shrink-0"
              style={{ borderTop: '1px solid rgba(255,255,255,0.05)', background: 'rgba(0,0,0,0.4)' }}
            >
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  onClick={() => send(s)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-bold uppercase tracking-wider whitespace-nowrap transition-all hover:bg-white/10 flex-shrink-0"
                  style={{
                    border: `1px solid ${agent.accentColor}44`,
                    color: agent.accentColor,
                    background: `${agent.accentColor}11`,
                  }}
                >
                  <Lightbulb className="w-3.5 h-3.5" />
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div
            className="flex gap-3 p-4 flex-shrink-0"
            style={{ borderTop: `1px solid ${agent.glowA}33`, background: 'rgba(10, 8, 30, 0.8)' }}
          >
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && send()}
              placeholder={isTestMode ? 'Solicitar asistencia...' : `Transmitir consulta a ${agent.name}...`}
              className="flex-1 px-4 py-3 text-[13px] font-medium rounded-xl outline-none text-white placeholder-white/30 transition-all"
              style={{
                border: `1px solid rgba(255,255,255,0.1)`,
                background: 'rgba(255,255,255,0.03)',
              }}
              onFocus={e => { e.target.style.borderColor = agent.accentColor; e.target.style.boxShadow = `0 0 15px ${agent.glowA}33`; }}
              onBlur={e  => { e.target.style.borderColor = 'rgba(255,255,255,0.1)'; e.target.style.boxShadow = 'none'; }}
            />
            <button
              onClick={() => send()}
              disabled={!input.trim() || loading}
              className="flex items-center justify-center w-12 h-12 rounded-xl flex-shrink-0 transition-all hover:scale-105 active:scale-95 disabled:opacity-40 disabled:hover:scale-100"
              style={{
                background: `linear-gradient(135deg, ${agent.gradientFrom}, ${agent.gradientTo})`,
                boxShadow: `0 0 15px ${agent.glowA}66`,
              }}
            >
              {loading
                ? <Loader2 className="w-5 h-5 text-white animate-spin" />
                : <Send className="w-5 h-5 text-white ml-0.5" />
              }
            </button>
          </div>
          </>
)}
        </div>
        
      )}

      <FABButton
        agent={agent}
        open={open}
        onClick={() => setOpen(p => !p)}
        isTestMode={isTestMode}
        hasUnread={hasUnread}
      />
    </div>
  );
};

export default MiniBotWidget;